import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useRef, useState } from "react";

import { saveUser } from "@/services/userServices/userActions";
import WebsitePermissionView from "./WebsitePermissionFormView";
import type { SitePermission } from "@/services/userServices/useCurrentPermission";

/**
 * The user's stored permission for this site, when an existing user is edited.
 * Its `siteId` stays `unknown` as CountryAccessDisplayContainer types the list.
 */
type StoredSitePermission = Pick<SitePermission, "read" | "edit" | "publish"> & {
  siteId?: unknown;
};

export type WebsitePermissionFormContainerProps = {
  siteId: string;
  CountryCode?: string;
  siteDomain?: string;
  siteName?: string;
  oldPermissionUpdateForm?: StoredSitePermission;
};

const WebsitePermissionContainer = ({
  siteId,
  CountryCode,
  siteDomain,
  siteName,
  oldPermissionUpdateForm,
}: WebsitePermissionFormContainerProps) => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.users.register_user);

  // Local permission state
  const [perm, setPerm] = useState<SitePermission>({
    read: true,
    edit: false,
    publish: false,
    siteId,
    CountryCode,
    siteDomain,
  });

  // UI toggles
  const [readOnly, setReadOnly] = useState(true);
  const [readEdit, setReadEdit] = useState(false);
  const [readEditPublish, setReadEditPublish] = useState(false);

  // Initialize once from props (no dispatch here!)
  const inited = useRef(false);
  useEffect(() => {
    if (inited.current) return;

    if (oldPermissionUpdateForm) {
      const { read, edit, publish } = oldPermissionUpdateForm;
      setPerm((p) => ({ ...p, read, edit, publish }));
      // reflect in toggles
      if (publish) {
        setReadOnly(false);
        setReadEdit(false);
        setReadEditPublish(true);
      } else if (edit) {
        setReadOnly(false);
        setReadEdit(true);
        setReadEditPublish(false);
      } else {
        setReadOnly(true);
        setReadEdit(false);
        setReadEditPublish(false);
      }
    }

    inited.current = true;
  }, [oldPermissionUpdateForm]);

  // helper to merge current site permission into user.permission
  const mergePermission = (nextPerm: SitePermission) => {
    const others = Array.isArray(user?.permission)
      ? user.permission.filter((p) => p?.siteId && p.siteId !== siteId)
      : [];
    return [...others, nextPerm];
  };

  // Only dispatch on user action
  const setReadOnlyOn = (checked: boolean) => {
    // read-only ON means {read:true, edit:false, publish:false}
    const next = { ...perm, read: true, edit: false, publish: false };
    setPerm(next);
    setReadOnly(true);
    setReadEdit(false);
    setReadEditPublish(false);
    dispatch(saveUser({ ...user, permission: mergePermission(next) }));
  };

  const setReadEditOn = (checked: boolean) => {
    // read & edit ON means {read:true, edit:true, publish:false}
    const next = { ...perm, read: true, edit: true, publish: false };
    setPerm(next);
    setReadOnly(false);
    setReadEdit(true);
    setReadEditPublish(false);
    dispatch(saveUser({ ...user, permission: mergePermission(next) }));
  };

  const setReadEditPublishOn = (checked: boolean) => {
    // read & edit & publish ON means {read:true, edit:true, publish:true}
    const next = { ...perm, read: true, edit: true, publish: true };
    setPerm(next);
    setReadOnly(false);
    setReadEdit(false);
    setReadEditPublish(true);
    dispatch(saveUser({ ...user, permission: mergePermission(next) }));
  };

  return (
    <WebsitePermissionView
      siteName={siteName}
      readOnly={readOnly}
      readEdit={readEdit}
      readEditPublish={readEditPublish}
      onReadOnlyChange={setReadOnlyOn}
      onReadEditChange={setReadEditOn}
      onReadEditPublishChange={setReadEditPublishOn}
    />
  );
};

export default WebsitePermissionContainer;
